<script setup>
import { computed, reactive } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  record: { type: Object, required: true },
  catalog: { type: Object, required: true },
  busy: { type: Boolean, default: false },
});
const emit = defineEmits(['save', 'cancel']);
const draft = reactive({
  name: props.record.name,
  description: props.record.description || '',
  permissions: [...props.record.permissions],
});
const implied = computed(
  () =>
    new Set(
      props.catalog.permissions
        .filter(permission => draft.permissions.includes(permission.id))
        .flatMap(permission => permission.implies)
    )
);
const selected = computed(
  () => new Set([...draft.permissions, ...implied.value])
);
const toggle = (id, checked) => {
  draft.permissions = checked
    ? [...draft.permissions, id]
    : draft.permissions.filter(permission => permission !== id);
};
const submit = () => {
  if (props.busy || !draft.name.trim()) return;
  emit('save', {
    ...draft,
    name: draft.name.trim(),
    permissions: [...draft.permissions],
  });
};
</script>

<template>
  <form
    class="flex flex-col gap-5 rounded-lg border border-n-weak p-5"
    @submit.prevent="submit"
  >
    <h2 class="text-lg font-medium text-n-slate-12">
      {{ record.id ? $t('PERMISSION_SETS.EDIT') : $t('PERMISSION_SETS.ADD') }}
    </h2>
    <label class="flex flex-col gap-2 text-n-slate-12">
      {{ $t('PERMISSION_SETS.NAME') }}
      <input
        v-model="draft.name"
        name="name"
        required
        :disabled="busy"
        class="w-full rounded border border-n-weak bg-n-solid-1 p-2"
      />
    </label>
    <label class="flex flex-col gap-2 text-n-slate-12">
      {{ $t('PERMISSION_SETS.DESCRIPTION') }}
      <textarea
        v-model="draft.description"
        name="description"
        :disabled="busy"
        class="w-full rounded border border-n-weak bg-n-solid-1 p-2"
      />
    </label>
    <fieldset class="flex flex-col gap-3" :disabled="busy">
      <legend class="mb-3 font-medium text-n-slate-12">
        {{ $t('PERMISSION_SETS.CAPABILITIES') }}
      </legend>
      <label
        v-for="permission in catalog.permissions"
        :key="permission.id"
        class="flex items-start gap-3 text-n-slate-12"
      >
        <input
          type="checkbox"
          :name="permission.id"
          :checked="selected.has(permission.id)"
          :disabled="implied.has(permission.id)"
          class="mt-1"
          @change="toggle(permission.id, $event.target.checked)"
        />
        <span class="flex flex-col gap-1">
          <span>{{ $t(`PERMISSION_SETS.CAPABILITY.${permission.id}`) }}</span>
          <template v-if="implied.has(permission.id)">
            <span class="text-sm text-n-slate-11">
              {{ $t('PERMISSION_SETS.INCLUDED') }}
            </span>
          </template>
        </span>
      </label>
    </fieldset>
    <p class="mb-0 text-sm text-n-slate-11">
      {{ $t('PERMISSION_SETS.EMPTY_PERMISSIONS') }}
    </p>
    <div class="flex gap-3">
      <Button
        type="submit"
        :label="$t('PERMISSION_SETS.SAVE')"
        :disabled="busy || !draft.name.trim()"
        :is-loading="busy"
      />
      <Button
        type="button"
        variant="ghost"
        :label="$t('PERMISSION_SETS.CANCEL')"
        :disabled="busy"
        @click="emit('cancel')"
      />
    </div>
  </form>
</template>
